/* eslint-disable jsx-a11y/anchor-is-valid */
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { Box, CircularProgress } from '@mui/material';
import { FormikProvider, getIn, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { CURRENCY, IS_SA, MIN_INVESTMENT } from '../../../../../../config/appConfig';
import { currencyFormatterHelper } from '../../../../../../helpers/currencyFormatterHelper';
import { FormHelper } from '../../../../../../helpers/formHelper';
import { DialogWrapper } from '../../../../../../shared/components/common/dialogWrapper';
import { FsButton } from '../../../../../../shared/components/button';
import { InputWrapper } from '../../../../../../shared/components/formUtilities/useInput';
import { TextWrapper } from '../../../../../../shared/components/formUtilities/useTextWrapper';
import { MarketplaceService } from '../../marketplaceService';

export const InvestBehalfDialog = ({
	state,
	onClose,
	onInvestment,
	remainingAmount = 0,
	minInvestmentAmount = MIN_INVESTMENT,
	loadingInvest = false,
	itemDetails = null,
	showCoupon = true
}) => {
	const marketplaceService = new MarketplaceService();
	const [loadingCode, setLoadingCode] = useState(false);
	const [estimatedProfit, setEstimatedProfit] = useState(0);
	const [promoCodeError, setPromoCodeError] = useState(null);
	const [showPromoCode, setShowPromoCode] = useState(false);
	const [couponDetails, setCouponDetails] = useState(null);
	if (minInvestmentAmount === 0) {
		minInvestmentAmount = MIN_INVESTMENT;
	}
	const { t } = useTranslation();
	const amounts = [
		{ name: '5,000', value: 5000 },
		{ name: '10,000', value: 10000 },
		{ name: !IS_SA ? '20,000' : '15,000', value: !IS_SA ? 20000 : 15000 },
		{ name: !IS_SA ? '36,000' : '18,000', value: !IS_SA ? 36000 : 18000 },
	];


	const getMinInvestAmount = () => {
		return currencyFormatterHelper(minInvestmentAmount);
	};

	const getRemainingAmount = () => {
		return currencyFormatterHelper(remainingAmount);
	};

	const getTotalProfit = () => {
		let fixed = itemDetails ? itemDetails?.fixed : 0;
		const tenure = itemDetails ? itemDetails?.month : 12;
		let amount = couponDetails?.totalInvestedAmount ? couponDetails?.totalInvestedAmount : form.values.amount;
		if (fixed) {
			let fixedInInt = parseFloat(fixed) / 100;
			setEstimatedProfit(parseFloat(amount) * fixedInInt * (tenure / 12));
			return;
		}

		setEstimatedProfit(0);
	};

	const InvestAmountSchema = yup.object().shape({
		amount: yup
			.number()
			.required(t('required'))
			.min(
				minInvestmentAmount,
				`${t('minimumCollectionInvestAmount')} ${getMinInvestAmount()}`
			)
			.max(
				remainingAmount,
				`${t('amountGreaterThanRemainingAmount')} ${getRemainingAmount()}`
			)
			.test('', t('enteredMustMultipleOf10'), (value) => {
				if (value % 10 > 0) {
					return false;
				}
				return true;
			})
			.nullable(),
	});

	const form = useFormik({
		initialValues: {
			amount: null,
			code: null,
		},
		validationSchema: InvestAmountSchema,
		onSubmit: () => {
			let formValues = JSON.parse(JSON.stringify(form.values));
			if (!showCoupon || !formValues.code || !couponDetails) {
				delete formValues.code;
			}
			if (couponDetails) {
				formValues["couponDetails"] = couponDetails;
			}
			onInvestment(formValues);
		},
		...FormHelper.formikProps(),
	});

	const formAmount = getIn(form.values, 'amount');

	const onInvest = () => {
		form.submitForm();
	};

	useEffect(() => {
		getTotalProfit();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formAmount, couponDetails]);

	const getTandCUrls = () => {
		return IS_SA
			? process.env.PUBLIC_URL + '/docs/TCs_Investors_KSA.docx'
			: process.env.PUBLIC_URL + '/docs/TC_Investors.pdf';
	};

	const onApplyCode = () => {
		if (!!!form.values?.code) {
			setPromoCodeError(t('pleaseAddAPromoCode'));
			form.setFieldTouched('code', true);
			return;
		}
		let formValue = {
			code: form.values?.code,
			loanId: itemDetails?.id,
			investedAmount: form.values?.amount
		};
		setLoadingCode(true);
		marketplaceService.checkPromoCode(formValue).then(resp => {
			setCouponDetails(resp.data);
			setLoadingCode(false);
			setPromoCodeError(null);
		}).catch(err => {
			setLoadingCode(false);
			if (err?.response?.data[0]?.message) {
				setPromoCodeError(err?.response?.data[0]?.message);
				form.setFieldTouched('code', true);
			}
		});
	};

	const onCloseAll = () => {
		setTimeout(() => {
			setCouponDetails(null);
			setShowPromoCode(false);
			setPromoCodeError(null);
		}, 500);
	};

	return (
		<DialogWrapper
			maxWidth="xs"
			width={'400px'}
			state={state}
			onClose={() => {
				onCloseAll();
				onClose(false);
				form.setTouched({ amount: false });
			}}
			title={t('confirmation')}
			content={
				<>
					<Box className="grid grid-flow-row gap-3">
						<TextWrapper
							classList="!text-center"
							text={t('amountWishToInvest')}
							type="h6"></TextWrapper>
						<Box className="grid grid-cols-4 gap-4">
							{amounts.map((resp, index) => {
								return (
									<Box
										key={index}
										onClick={() => {
											if (couponDetails) {
												return;
											}
											form.setFieldValue('amount', resp.value);
										}}
										className={`border p-1 flex justify-center items-center cursor-pointer hover:primary-bg hover:text-white rounded-sm hover:border-0 ${couponDetails ? 'bg-[#f5f5f5] pointer-events-none' : ''}`}>
										{resp.name}
									</Box>
								);
							})}
						</Box>
						<FormikProvider value={form}>
							<InputWrapper
								disabled={couponDetails}
								isCurrencyText
								name="amount"
								key={'amount'}
								label={t('amount')}></InputWrapper>
							<Box>
								<Box className={`mb-4 ${showPromoCode ? 'mt-3' : 'mt-0 !text-end'}`}>
									{!showPromoCode && showCoupon ? <><a
										onClick={() => setShowPromoCode(!showPromoCode)}
										target="_blank"
										rel="noreferrer"
										className="!text-[11px] underline-link"
									>
										{t('doYouHavePromoCode')}
									</a></> : null}
									{!couponDetails ?
										<fieldset disabled={!form.values.amount}>
											{showPromoCode ? <InputWrapper
												endIcon={
													<Box onClick={() => {
														if (loadingCode) {
															return;
														}
														onApplyCode();
													}}
														className={`primary-bg apply-btn cursor-pointer shadow-md rounded-md px-4 text-white 
													font-semibold py-0.5 !text-[11px] -mr-[8px] -ml-[8px] flex items-center justify-center`}>
														{!loadingCode ? <>{t('apply')}</> : <Box className="flex items-center justify-center">
															<CircularProgress size={12} className='mx-1 !text-white' />
															<Box>{`${t('applying')}...`}</Box>
														</Box>}
													</Box>
												}
												onFocus={() => {
													if (promoCodeError) {
														setPromoCodeError('');
														form.setFieldValue('code', null);
														form.setFieldTouched('code', true);
													}
												}}
												externalErrorMessage={promoCodeError}
												name="code"
												key={'code'}
												label={t('promoCode')}></InputWrapper> : null}
										</fieldset> :
										<Box className='flex flex-col'>
											<Box className="flex !text-[13px] items-center p-2 rounded-md justify-between primary-border border border-dashed">
												<Box className="flex items-center">
													<CheckCircleOutlineOutlinedIcon color='primary' className='!text-[24px]' />
													<Box className='w-1.5'></Box>
													<Box className='flex flex-col'>
														<Box className='flex'>
															<span className='font-[500]'>{`${couponDetails.code}`}</span>
															<span className='mx-1'>{`${t('applied')}`}</span>
														</Box>
														<Box className='text-[#6b6b6b] !text-[11px]'>
															<span className='currency-symbol'>{CURRENCY}</span>
															{` ${currencyFormatterHelper(couponDetails?.cashbackAmount)} ${t('cashback')}`}
														</Box>
													</Box>
												</Box>
												<Box onClick={() => {
													form.setFieldValue('code', null);
													setCouponDetails(null);
													setPromoCodeError(null);
												}} className='text-primary cursor-pointer font-[500]'>{t('remove')}</Box>
											</Box>
										</Box>
									}
								</Box>
								<Box className='mt-3 mb-4 space-y-2'>
									{couponDetails ? <Box className='flex items-center justify-between !text-[12px]'>
										<Box>{t('amount')}</Box>
										<Box className="font-[500]">
											<span className='currency-symbol'>{CURRENCY}</span>
											{` ${currencyFormatterHelper(parseFloat(form.values.amount))}`}</Box>
									</Box> : null}
									{couponDetails ? <Box className='flex items-center justify-between !text-[12px]'>
										<Box>{t('couponApplied')}</Box>
										<Box className="font-[500]">
											(+) <span className='currency-symbol'>{CURRENCY}</span> {` ${currencyFormatterHelper(couponDetails?.cashbackAmount)}`}</Box>
									</Box> : null}
									{couponDetails ? <Box className='flex items-center justify-between !text-[12px]'>
										<Box>{t('total')}</Box>
										<Box className="font-[500]">
											<span className='currency-symbol'>{CURRENCY}</span>{` ${currencyFormatterHelper(couponDetails?.totalInvestedAmount)}`}</Box>
									</Box> : null}
									<Box className='flex items-center justify-between !text-[12px]'>
										<Box>{t('estimatedProfit')}</Box>
										<Box className="font-[500] text-primary"><span className='currency-symbol'>{CURRENCY}</span> {` ${currencyFormatterHelper(estimatedProfit)}`}</Box>
									</Box>
								</Box>
								<span className="!text-[11px]">
									<p className="caption">
										<span className="caption">As per the</span>
										<a
											target="_blank"
											rel="noreferrer"
											className="!text-[11px] underline-link"
											href={getTandCUrls()}>
											&nbsp;T&amp;Cs for Investors,&nbsp;
										</a>
										the provided risk band classification is only an indication
										and you need to form your own opinion regarding the
										creditworthiness of the Business. The financing contract will
										be directly between you (and other investors) and the
										Business. Placing this order is final so please seek
										independent advice if in doubt.
									</p>
								</span>
							</Box>
						</FormikProvider>
					</Box>
				</>
			}
			actions={
				<Box className="flex items-center">
					<FsButton
						dataTestID='investCancelBtn'
						id={IS_SA ? 'abandoned_cart_sa' : 'abandoned_cart_ae'}
						variant="outline"
						disabled={loadingInvest}
						onClick={() => {
							onCloseAll();
							form.setValues({ amount: null, code: null });
							form.setTouched({ amount: false });
							onClose(false);
						}}>{t('cancel')}</FsButton>
					<FsButton
						dataTestID='investBtn'
						id={IS_SA ? 'add_to_cart_sa' : 'add_to_cart_ae'}
						loading={loadingInvest}
						disabled={(!!promoCodeError && !!form.values.code) || loadingCode}
						className="!mx-2 invest-amount-btn"
						onClick={() => {
							onInvest();
						}}>{t('invest')}</FsButton>
				</Box>
			}></DialogWrapper>
	);
};
